import { PlusOutlined } from "@ant-design/icons";
import { Modal, Row } from "antd";
import { AsyncButton } from "components/async_clickables";
import FastTooltip from "components/fast_tooltip";
import {
  NewVolumeLayerSelection,
  RestrictMagnificationSlider,
} from "dashboard/advanced_dataset/create_annotation_modal";
import { useWkSelector } from "libs/react_hooks";
import Toast from "libs/toast";
import clone from "lodash-es/clone";
import differenceWith from "lodash-es/differenceWith";
import messages from "messages";
import type React from "react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import type { APIDataset, APISegmentationLayer } from "types/api_types";
import { MappingStatusEnum } from "viewer/constants";
import { isEditingAnnotationLayerSetDisabled } from "viewer/model/accessors/annotation_accessor";
import {
  getLayerByName,
  getMagInfo,
  getMappingInfo,
  getSegmentationLayers,
  getSomeMagInfoForDataset,
} from "viewer/model/accessors/dataset_accessor";
import {
  getAllReadableLayerNames,
  getVolumeTracingLayers,
} from "viewer/model/accessors/volumetracing_accessor";
import { pushSaveQueueTransactionIsolated } from "viewer/model/actions/save_actions";
import { addLayerToAnnotation } from "viewer/model/sagas/volume/update_actions";
import { api, Model } from "viewer/singletons";
import Store from "viewer/store";
import InputComponent from "viewer/view/components/input_component";

export type ValidationResult = { isValid: boolean; message: string };
function checkForLayerNameDuplication(
  readableLayerName: string,
  allReadableLayerNames: string[],
): ValidationResult {
  const layerNameDoesNotExist = !allReadableLayerNames.includes(readableLayerName);
  return {
    isValid: layerNameDoesNotExist,
    message: layerNameDoesNotExist ? "" : messages["tracing.volume_layer_name_duplication"],
  };
}

function checkLayerNameForInvalidCharacters(readableLayerName: string): ValidationResult {
  // A layer name is not allowed to start with a dot.
  if (readableLayerName.startsWith(".")) {
    return {
      isValid: false,
      message: messages["tracing.volume_layer_name_starts_with_dot"],
    };
  }
  const validLayerNameCharactersRegex = /[0-9a-zA-Z-._$]+/g;
  // Removing all URISaveCharacters from readableLayerName. The leftover chars are all invalid.
  const allInvalidChars = readableLayerName.replace(validLayerNameCharactersRegex, "");
  const allUniqueInvalidCharsAsSet = new Set(allInvalidChars);
  const allUniqueInvalidCharsAsString = "".concat(...allUniqueInvalidCharsAsSet.values());
  const isValid = allUniqueInvalidCharsAsString.length === 0;
  return {
    isValid,
    message: isValid
      ? ""
      : messages["tracing.volume_layer_name_includes_invalid_characters"](
          allUniqueInvalidCharsAsString,
        ),
  };
}

export function validateReadableLayerName(
  readableLayerName: string,
  allReadableLayerNames: string[],
  nameNotToCount?: string,
): ValidationResult {
  if (readableLayerName.length < 1) {
    return {
      isValid: false,
      message: messages["tracing.volume_layer_name_too_short"],
    };
  }
  if (nameNotToCount) {
    // nameNotToCount needs to be removed once if it is included in allReadableLayerNames.
    // This is needed in case of saving an existing volume layer's name when the name was not modified.
    // In this scenario nameNotToCount should be the previous name of the volume layer which will then be removed once from the allReadableLayerNames.
    // Thus there is only a duplication of the given nameNotToCount if an additional other layer already has nameNotToCount as a name
    // and the readableLayerName is equal to the name given by nameNotToCount.
    const index = allReadableLayerNames.indexOf(nameNotToCount);
    if (index > -1) {
      allReadableLayerNames = clone(allReadableLayerNames); // Avoiding modifying passed parameters.
      allReadableLayerNames.splice(index, 1);
    }
  }
  const duplicatedNameResult = checkForLayerNameDuplication(
    readableLayerName,
    allReadableLayerNames,
  );
  if (!duplicatedNameResult.isValid) {
    return duplicatedNameResult;
  }
  const invalidCharactersResult = checkLayerNameForInvalidCharacters(readableLayerName);
  return invalidCharactersResult;
}

export default function AddVolumeLayerModal({
  dataset,
  onCancel,
  preselectedLayerName,
  disableLayerSelection,
}: {
  dataset: APIDataset;
  onCancel: () => void;
  preselectedLayerName: string | undefined;
  disableLayerSelection: boolean | undefined;
}) {
  const [selectedSegmentationLayerName, setSelectedSegmentationLayerName] = useState<
    string | undefined
  >(preselectedLayerName);
  const dispatch = useDispatch();
  const annotation = useWkSelector((state) => state.annotation);
  const { isDisabled: mayNotEditLayerSet, explanation: reasonForCantEditLayerSet } = useWkSelector(
    (state) => isEditingAnnotationLayerSetDisabled(state),
  );
  const allReadableLayerNames = useMemo(
    () => getAllReadableLayerNames(dataset, annotation),
    [dataset, annotation],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: Needs investigation whether to add more dependencies.
  const initialNewLayerName = useMemo(() => {
    if (preselectedLayerName) {
      return preselectedLayerName;
    }
    if (allReadableLayerNames.indexOf("Volume") === -1) {
      return "Volume";
    } else {
      let counter = 1;
      let name = `Volume${counter}`;
      while (allReadableLayerNames.indexOf(name) >= 0) {
        // Increase number at the end of name until a valid initial name is found.
        ++counter;
        name = `Volume${counter}`;
      }
      return name;
    }
  }, [dataset, annotation]);
  const selectedSegmentationLayer =
    selectedSegmentationLayerName != null
      ? (getLayerByName(dataset, selectedSegmentationLayerName) as APISegmentationLayer)
      : null;
  const [newLayerName, setNewLayerName] = useState(initialNewLayerName);

  const magInfo =
    selectedSegmentationLayer == null
      ? getSomeMagInfoForDataset(dataset)
      : getMagInfo(selectedSegmentationLayer.mags);
  const [magIndices, setMagIndices] = useState([0, 10000]);

  const handleSetNewLayerName = (evt: React.ChangeEvent<HTMLInputElement>) =>
    setNewLayerName(evt.target.value);

  const segmentationLayers = getSegmentationLayers(dataset);
  const volumeTracingLayers = getVolumeTracingLayers(dataset);

  const availableSegmentationLayers = differenceWith(segmentationLayers, volumeTracingLayers);

  const handleAddVolumeLayer = async () => {
    await api.tracing.save();
    const validationResult = validateReadableLayerName(
      newLayerName,
      allReadableLayerNames,
      selectedSegmentationLayerName,
    );
    if (!validationResult.isValid) {
      Toast.error(validationResult.message);
      return;
    }
    const minMagAllowed = Math.max(...magInfo.getMagByIndexOrThrow(magIndices[0]));
    const maxMagAllowed = Math.max(...magInfo.getMagByIndexOrThrow(magIndices[1]));

    if (selectedSegmentationLayerName == null) {
      dispatch(
        pushSaveQueueTransactionIsolated(
          addLayerToAnnotation({
            typ: "Volume",
            name: newLayerName,
            fallbackLayerName: undefined,
            magRestrictions: {
              min: minMagAllowed,
              max: maxMagAllowed,
            },
          }),
        ),
      );
      await Model.ensureSavedState();
    } else {
      if (selectedSegmentationLayer == null) {
        throw new Error("Segmentation layer is null");
      }
      const fallbackLayerName = selectedSegmentationLayer.name;

      const mappingInfo = getMappingInfo(
        Store.getState().temporaryConfiguration.activeMappingByLayer,
        selectedSegmentationLayerName,
      );
      let maybeMappingName = null;
      if (
        mappingInfo.mappingStatus !== MappingStatusEnum.DISABLED &&
        mappingInfo.mappingType !== "JSON"
      ) {
        maybeMappingName = mappingInfo.mappingName;
      }

      dispatch(
        pushSaveQueueTransactionIsolated(
          addLayerToAnnotation({
            typ: "Volume",
            name: newLayerName,
            fallbackLayerName,
            magRestrictions: {
              min: minMagAllowed,
              max: maxMagAllowed,
            },
            mappingName: maybeMappingName,
          }),
        ),
      );
      await Model.ensureSavedState();
    }

    await api.tracing.hardReload();
  };

  return (
    <Modal title="Add Volume Annotation Layer" footer={null} onCancel={onCancel} open>
      Layer Name:{" "}
      <InputComponent
        size="small"
        onChange={handleSetNewLayerName}
        value={newLayerName}
        style={{
          width: "60%",
          marginBottom: 16,
          marginLeft: 8,
        }}
      />
      {availableSegmentationLayers.length > 0 ? (
        <NewVolumeLayerSelection
          dataset={dataset}
          segmentationLayers={availableSegmentationLayers}
          selectedSegmentationLayerName={selectedSegmentationLayerName}
          setSelectedSegmentationLayerName={setSelectedSegmentationLayerName}
          disableLayerSelection={disableLayerSelection ?? false}
        />
      ) : null}
      <RestrictMagnificationSlider
        magInfo={magInfo}
        selectedSegmentationLayer={selectedSegmentationLayer}
        magIndices={magIndices}
        setMagIndices={setMagIndices}
      />
      <Row justify="center" align="middle">
        <FastTooltip title={reasonForCantEditLayerSet}>
          <AsyncButton
            onClick={handleAddVolumeLayer}
            type="primary"
            icon={<PlusOutlined />}
            disabled={mayNotEditLayerSet}
          >
            Add Volume Annotation Layer
          </AsyncButton>
        </FastTooltip>
      </Row>
    </Modal>
  );
}
